/**
 * Bidirectional float mapping utilities for the Morph engine.
 * Ported from androidx.graphics.shapes.FloatMapping.kt
 *
 * Used to map outline progress values between two shapes that have
 * different feature distributions.
 */

import { interpolate, positiveModulo } from "../utils/utils";

/**
 * A segment in a piecewise-linear mapping.
 * Maps [fromStart, fromEnd] → [toStart, toEnd].
 */
export interface Segment {
	readonly fromStart: number;
	readonly fromEnd: number;
	readonly toStart: number;
	readonly toEnd: number;
}

/**
 * Maps a value from the "from" domain to the "to" domain using a segment list.
 * @param value - Input value in the from domain
 * @param segments - Sorted segment list
 * @returns Mapped value in the to domain
 */
export function mapSegments(value: number, segments: Segment[]): number {
	for (const seg of segments) {
		if (value <= seg.fromEnd) {
			if (seg.fromEnd === seg.fromStart) return seg.toStart;
			const t = (value - seg.fromStart) / (seg.fromEnd - seg.fromStart);
			return interpolate(seg.toStart, seg.toEnd, t);
		}
	}
	// Past the end — use last segment
	const last = segments[segments.length - 1];
	return last.toEnd;
}

/**
 * DoubleMapper: bidirectional mapper used in the Morph matching algorithm.
 * Maps progress values from polygon1 space → polygon2 space and vice versa.
 */
export interface DoubleMapper {
	/** Maps a progress value from the start polygon to the end polygon. */
	map(progress: number): number;
	/** Maps a progress value from the end polygon back to the start polygon. */
	mapBack(progress: number): number;
}

/**
 * Creates a DoubleMapper from two parallel arrays of mapped progress values.
 * Used internally by featureMapper.
 *
 * @param fromValues - Progress values in polygon1 space [0..1]
 * @param toValues - Corresponding progress values in polygon2 space [0..1]
 */
export function createDoubleMapper(
	fromValues: number[],
	toValues: number[],
): DoubleMapper {
	if (fromValues.length !== toValues.length || fromValues.length === 0) {
		// Identity mapper for degenerate cases
		return { map: (v) => v, mapBack: (v) => v };
	}

	const n = fromValues.length;

	// Build forward segments: from1 → to2
	const forwardSegs: Segment[] = [];
	for (let i = 0; i < n; i++) {
		const next = (i + 1) % n;
		const fromStart = fromValues[i];
		let fromEnd = fromValues[next];
		const toStart = toValues[i];
		let toEnd = toValues[next];
		// Handle wrap-around
		if (fromEnd < fromStart) fromEnd += 1;
		if (toEnd < toStart) toEnd += 1;
		forwardSegs.push({ fromStart, fromEnd, toStart, toEnd });
	}

	// Build reverse segments: to2 → from1
	const reverseSegs: Segment[] = [];
	for (const seg of forwardSegs) {
		reverseSegs.push({
			fromStart: seg.toStart,
			fromEnd: seg.toEnd,
			toStart: seg.fromStart,
			toEnd: seg.fromEnd,
		});
	}
	reverseSegs.sort((a, b) => a.fromStart - b.fromStart);

	return {
		map: (v) =>
			positiveModulo(mapSegments(positiveModulo(v, 1), forwardSegs), 1),
		mapBack: (v) =>
			positiveModulo(mapSegments(positiveModulo(v, 1), reverseSegs), 1),
	};
}
